// AgentForge V1's own WebSocket connection.
//
// Same URL legacy uses (`wss://${host}/agentforge/api/v1/ws?token=...`)
// but a separate socket — V1 is self-contained and doesn't share the
// singleton from `useMissionControlWS`. Mount this exactly once
// (in the `AgentForge` umbrella) so each WS frame is processed a
// single time.
//
// Mirrors legacy's lifecycle:
//   • Auto-connect on mount (reads `apiv1Auth` from localStorage).
//   • Auto-reconnect with exponential backoff (1s → 30s cap).
//   • On tab-visibility-restored: reconnect immediately if dead.
//   • Subscribers receive raw `{type, payload}` frames.
//
// We deliberately don't include a Redux cache-invalidation bus here
// (legacy's `invalidateTags`); V1 uses TanStack Query, so any cache
// effects happen via `queryClient.invalidateQueries` inside the
// individual event handlers (see `useAgentForgeStream`).

import { useCallback, useEffect, useRef, useState } from 'react';

// Wire format from AgentForge WS: { type: "<event_type>", payload: {...} }
export interface AgentForgeWSMessage {
  type: string;
  payload: Record<string, any>;
}

type MessageHandler = (_msg: AgentForgeWSMessage) => void;

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;

const buildWsUrl = (token: string): string => {
  const agentforgeUrl = import.meta.env.VITE_AGENTFORGE_URL;
  if (agentforgeUrl) {
    const proto = agentforgeUrl.startsWith('https') ? 'wss' : 'ws';
    const host = new URL(agentforgeUrl).host;
    return `${proto}://${host}/agentforge/api/v1/ws?token=${token}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/agentforge/api/v1/ws?token=${token}`;
};

export interface UseAgentForgeWSResult {
  connected: boolean;
  reconnecting: boolean;
  subscribe: (_handler: MessageHandler) => () => void;
}

export const useAgentForgeWS = (): UseAgentForgeWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const handlersRef = useRef<Set<MessageHandler>>(new Set());
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const mountedRef = useRef(true);
  const wasConnectedRef = useRef(false);
  const connectRef = useRef<() => void>();

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  // Stable subscribe: register/unregister a handler. Multiple subscribers
  // can coexist; each receives every frame the socket delivers.
  const subscribe = useCallback((handler: MessageHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    // Don't double-connect.
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('apiv1Auth');
    if (!token) {
      // Surface this in dev — without a token at this origin the socket
      // never opens, which leaves the spinner spinning forever.
      console.warn(
        '[agentforge] no `apiv1Auth` token in localStorage at this origin — WS will not connect. Log in on this origin first.'
      );
      return;
    }

    const url = buildWsUrl(token);
    console.info(
      '[agentforge] connecting WS:',
      url.replace(/token=[^&]+/, 'token=…')
    );
    const ws = new WebSocket(url);

    ws.onopen = () => {
      wasConnectedRef.current = true;
      setConnected(true);
      setReconnecting(false);
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
    };

    ws.onclose = () => {
      setConnected(false);
      if (wasConnectedRef.current) setReconnecting(true);
      wsRef.current = null;
      if (!mountedRef.current) return;
      // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (capped)
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimeoutRef.current = setTimeout(
        () => connectRef.current?.(),
        delay
      );
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as AgentForgeWSMessage;
        handlersRef.current.forEach((handler) => handler(msg));
      } catch {
        // Ignore malformed frames.
      }
    };

    wsRef.current = ws;
  }, []);

  // Keep `connectRef` in sync so the reconnect timer always calls the
  // latest closure.
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Open the socket on mount, close on unmount.
  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Reconnect immediately when the tab returns to the foreground if the
  // socket dropped while it was hidden.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  return { connected, reconnecting, subscribe };
};
